import { useMutation } from '@tanstack/react-query'
import axios from 'axios'
import { uploadFile as uploadFileRequest } from './generated/data-transformation/data-transformation'

export interface UploadFileRequest {
  file: File
  partnerOrganizationId?: string
  documentType: string
  userOrgType: string
  isHeadless?: boolean
  onProgress?: (percent: number) => void
}

/** The outcome the upload hook reports, success or failure; not the API's response. */
export interface UploadFileOutcome {
  fileName: string
  success: boolean
  error?: string
}

/**
 * Upload a file to the data transformation service.
 * The file is uploaded to the backend API which handles:
 * - S3 upload
 * - SNS notification
 * - Partnership validation
 */
export const uploadFile = async (
  request: UploadFileRequest
): Promise<UploadFileOutcome> => {
  try {
    await uploadFileRequest(
      {
        file: request.file,
        // An empty partner is left out, as before.
        partnerOrganizationId: request.partnerOrganizationId || undefined,
        documentType: request.documentType,
        userOrgType: request.userOrgType,
        isHeadless: request.isHeadless
      },
      {
        onUploadProgress: (event) => {
          if (!request.onProgress) return
          const total = event.total ?? request.file.size
          if (!total) return
          const percent = Math.min(
            100,
            Math.round((event.loaded / total) * 100)
          )
          request.onProgress(percent)
        }
      }
    )

    return {
      fileName: request.file.name,
      success: true
    }
  } catch (error) {
    let errorMessage = 'Upload failed'

    if (axios.isAxiosError(error)) {
      errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.response?.statusText ||
        error.message
    } else if (error instanceof Error) {
      errorMessage = error.message
    }

    return {
      fileName: request.file.name,
      success: false,
      error: errorMessage
    }
  }
}

/**
 * React Query mutation hook for uploading files.
 *
 * Usage:
 * ```tsx
 * const { mutateAsync: uploadFile, isPending } = useUploadFile()
 *
 * const result = await uploadFile({
 *   file: myFile,
 *   partnerOrganizationId: 'abc-123',
 *   documentType: 'SPA',
 *   userOrgType: 'Manufacturer'
 * })
 * ```
 */
export const useUploadFile = () => {
  return useMutation({
    mutationFn: uploadFile
  })
}
